import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import { useConversations } from '../../contexts/ConversationsContext';
import { useMobileDrawer } from '../../contexts/MobileDrawerContext';
import type { ConversationSummary } from '../../types/api';
import { IconMore } from '../primitives/Icons';
import { ActionSheet } from './ActionSheet';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { EditableTitle } from './EditableTitle';
import { RowMenu } from './RowMenu';

interface AnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Long-press threshold for mobile context-menu on a row. */
const LONG_PRESS_MS = 450;

export function ConversationList() {
  const { t } = useTranslation();
  const { conversations, loading, rename, remove } = useConversations();
  const { runState } = useActiveConversation();
  const { close, isMobile } = useMobileDrawer();
  const { id: activeId } = useParams();
  const navigate = useNavigate();

  // Conversation switching is locked during streaming — mirror that for the
  // ⋯ menu, since deleting the active row mid-stream tears down the SSE.
  const isStreaming = runState === 'streaming' || runState === 'sending';

  // Only one of these is non-null at a time. menuFor is desktop popover state
  // (carries the anchor rect); sheetFor is mobile action-sheet state.
  const [menuFor, setMenuFor] = useState<{ id: string; rect: AnchorRect } | null>(null);
  const [sheetFor, setSheetFor] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDelFor, setConfirmDelFor] = useState<string | null>(null);

  const closeAllMenus = () => {
    setMenuFor(null);
    setSheetFor(null);
  };

  // Opening a menu uses different surfaces per platform.
  const openMenuFor = (id: string, rect: AnchorRect) => {
    if (isMobile) {
      setSheetFor(id);
    } else {
      setMenuFor({ id, rect });
    }
  };

  const handleRenameFromMenu = (id: string) => {
    closeAllMenus();
    setRenamingId(id);
  };

  const handleDeleteFromMenu = (id: string) => {
    closeAllMenus();
    setConfirmDelFor(id);
  };

  // After a confirmed delete: if the deleted id is the routed active one,
  // navigate to the next conversation in the visible list (or the previous
  // one if the deleted was last, or to /c/new if the list will be empty).
  const handleConfirmDelete = async () => {
    const id = confirmDelFor;
    if (!id) return;
    setConfirmDelFor(null);
    if (id === activeId) {
      const idx = conversations.findIndex((c) => c.id === id);
      const next = conversations[idx + 1] ?? conversations[idx - 1];
      navigate(next ? `/c/${next.id}` : '/c/new', { replace: true });
    }
    await remove(id);
  };

  const handleSelect = (id: string) => {
    navigate(`/c/${id}`);
    close();
  };

  const buckets = bucketByRecency(conversations);

  if (loading && conversations.length === 0) {
    return <div className="sidebar-empty">{t('loading')}</div>;
  }
  if (conversations.length === 0) {
    return <div className="sidebar-empty">{t('noConversations')}</div>;
  }

  const renderItem = (c: ConversationSummary) => (
    <ConvRow
      key={c.id}
      conv={c}
      isActive={c.id === activeId}
      isStreaming={isStreaming}
      isMobile={isMobile}
      isRenaming={renamingId === c.id}
      onSelect={handleSelect}
      onCommitRename={(id, title) => {
        setRenamingId(null);
        void rename(id, title);
      }}
      onStartRename={(id) => setRenamingId(id)}
      onCancelRename={() => setRenamingId(null)}
      onOpenMenu={openMenuFor}
    />
  );

  // The conversation backing the open menu/sheet/dialog. Read fresh each
  // render so the title in the sheet/dialog reflects optimistic rename
  // updates immediately.
  const menuConv = menuFor ? conversations.find((c) => c.id === menuFor.id) : null;
  const sheetConv = sheetFor ? conversations.find((c) => c.id === sheetFor) : null;
  const confirmConv = confirmDelFor ? conversations.find((c) => c.id === confirmDelFor) : null;

  return (
    <>
      <div className="sidebar-chats">
        {buckets.today.length > 0 && (
          <>
            <div className="sidebar-section-label">{t('today')}</div>
            {buckets.today.map(renderItem)}
          </>
        )}
        {buckets.yesterday.length > 0 && (
          <>
            <div className="sidebar-section-label">{t('yesterday')}</div>
            {buckets.yesterday.map(renderItem)}
          </>
        )}
        {buckets.earlier.length > 0 && (
          <>
            <div className="sidebar-section-label">{t('earlier')}</div>
            {buckets.earlier.map(renderItem)}
          </>
        )}
      </div>

      {/* Desktop popover. Only one open at a time; managed by menuFor. */}
      {menuFor && menuConv && (
        <RowMenu
          anchorRect={menuFor.rect}
          onRename={() => handleRenameFromMenu(menuFor.id)}
          onDelete={() => handleDeleteFromMenu(menuFor.id)}
          onClose={() => setMenuFor(null)}
        />
      )}

      {/* Mobile action sheet. */}
      {sheetFor && sheetConv && (
        <ActionSheet
          title={sheetConv.title || t('untitled')}
          onRename={() => handleRenameFromMenu(sheetFor)}
          onDelete={() => handleDeleteFromMenu(sheetFor)}
          onClose={() => setSheetFor(null)}
        />
      )}

      {/* Shared confirm-delete dialog (both desktop and mobile paths). */}
      {confirmDelFor && (
        <ConfirmDeleteDialog
          title={confirmConv?.title || t('untitled')}
          onCancel={() => setConfirmDelFor(null)}
          onConfirm={() => void handleConfirmDelete()}
        />
      )}
    </>
  );
}

interface ConvRowProps {
  conv: ConversationSummary;
  isActive: boolean;
  isStreaming: boolean;
  isMobile: boolean;
  isRenaming: boolean;
  onSelect: (id: string) => void;
  onCommitRename: (id: string, title: string) => void;
  onStartRename: (id: string) => void;
  onCancelRename: () => void;
  onOpenMenu: (id: string, rect: AnchorRect) => void;
}

function ConvRow({
                   conv,
                   isActive,
                   isStreaming,
                   isMobile,
                   isRenaming,
                   onSelect,
                   onCommitRename,
                   onStartRename,
                   onCancelRename,
                   onOpenMenu,
                 }: ConvRowProps) {
  const { t } = useTranslation();
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const pressTimer = useRef<number | null>(null);
  // True when a long-press just fired; suppresses the synthetic click that
  // some browsers dispatch on touchend after the press.
  const longPressedRef = useRef(false);

  // Switching is locked during streaming for non-active rows (mirrors the
  // existing list behavior). Active row stays clickable so the user can
  // still focus the chat — they just can't open the ⋯ menu.
  const disabled = isStreaming && !isActive;
  // Hide the ⋯ menu entirely during streaming to avoid the deletion-mid-SSE
  // edge case. The chat-header rename path still works.
  const showMore = !isStreaming && !isRenaming;

  const fireOpenMenu = () => {
    const el = moreRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    onOpenMenu(conv.id, { left: r.left, top: r.top, width: r.width, height: r.height });
  };

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    fireOpenMenu();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    // Always preventDefault — on mobile this stops the browser's native
    // context menu from appearing on top of our long-press action sheet.
    e.preventDefault();
    if (isMobile) return; // mobile uses long-press; this is the desktop path
    if (isStreaming || isRenaming) return;
    fireOpenMenu();
  };

  const handleTouchStart = () => {
    if (!isMobile || isStreaming || isRenaming) return;
    longPressedRef.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPressedRef.current = true;
      fireOpenMenu();
    }, LONG_PRESS_MS);
  };

  const cancelLongPress = () => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = () => {
    // If a long-press just opened the menu, consume the trailing click that
    // some mobile browsers dispatch on touchend.
    if (longPressedRef.current) {
      longPressedRef.current = false;
      return;
    }
    if (isRenaming || disabled) return;
    onSelect(conv.id);
  };

  return (
    <div
      className={
        `chat-item${isActive ? ' active' : ''}` +
        `${disabled ? ' disabled' : ''}` +
        `${isRenaming ? ' editing' : ''}`
      }
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (isRenaming || disabled) return;
        if (e.key === 'Enter') onSelect(conv.id);
      }}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={cancelLongPress}
      onTouchMove={cancelLongPress}
      onTouchCancel={cancelLongPress}
    >
      <EditableTitle
        value={conv.title}
        placeholder={t('untitled')}
        onCommit={(v) => onCommitRename(conv.id, v)}
        editing={isRenaming}
        setEditing={(v) => {
          // Double-click on the row title enters rename mode (mirrors the
          // design prototype). The menu paths also call onStartRename via
          // their own handlers in the parent.
          if (v) onStartRename(conv.id);
          else onCancelRename();
        }}
        className="chat-item-title"
        inputClassName="chat-item-input"
        inputAriaLabel={t('renameConversation')}
      />
      {showMore && (
        <button
          ref={moreRef}
          type="button"
          className="row-more"
          onClick={handleMoreClick}
          aria-label={t('moreOptions')}
        >
          <IconMore />
        </button>
      )}
    </div>
  );
}

interface Buckets {
  today: ConversationSummary[];
  yesterday: ConversationSummary[];
  earlier: ConversationSummary[];
}

function bucketByRecency(items: ConversationSummary[]): Buckets {
  const today = startOfDay(new Date()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;

  const buckets: Buckets = { today: [], yesterday: [], earlier: [] };
  for (const c of items) {
    const ts = new Date(c.updated_at).getTime();
    if (ts >= today) buckets.today.push(c);
    else if (ts >= yesterday) buckets.yesterday.push(c);
    else buckets.earlier.push(c);
  }
  return buckets;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
